// PrioritySelector — faithful port of sampler/src/components/PrioritySelector.vue.
// A pill that opens a dropdown of urgent/normal/low (editable) with dot + check.

import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import { PRIORITIES, priorityMeta } from './lib'
import { useT } from '../../i18n'
import { samplesCommonI18n } from '../../i18n/samples/common'

export function PrioritySelector({
  value,
  editable = true,
  size = 'md',
  onChange,
}: {
  value: string
  editable?: boolean
  size?: 'sm' | 'md'
  onChange: (key: string) => void
}) {
  const sc = useT(samplesCommonI18n)
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const meta = priorityMeta(value)
  const sizeClass = size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs'

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('click', onDoc)
    return () => document.removeEventListener('click', onDoc)
  }, [open])

  return (
    <div ref={wrapperRef} role="presentation" className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`inline-flex items-center gap-1.5 rounded-full border font-medium transition ${meta.chip} ${sizeClass} ${editable ? 'cursor-pointer hover:brightness-95' : 'cursor-default'}`}
        disabled={!editable}
        onClick={(e) => {
          if (!editable) return
          e.stopPropagation()
          setOpen((o) => !o)
        }}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
        {sc.priorities[meta.key]}
        {editable && (
          <ChevronDown size={10} className={open ? 'rotate-180 transition' : 'transition'} aria-hidden="true" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-36 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-1 shadow-lg">
          {PRIORITIES.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-[var(--color-surface-muted)] ${p.key === value ? 'font-semibold text-[var(--color-text)]' : 'text-[var(--color-text)]'}`}
              onClick={(e) => {
                e.stopPropagation()
                setOpen(false)
                if (p.key !== value) onChange(p.key)
              }}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${p.dot}`} />
              <span className="flex-1">{sc.priorities[p.key]}</span>
              {p.key === value && <Check size={12} aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
